import React, { useEffect, useState } from 'react'
import {
  Btn,
  CustomCheckbox,
  CustomModal,
  InputNumeroDecimal,
  SectionTitle,
} from '../../../../../components'
import { getUfs } from '../../../../../services/api/https/system/system.http'
import { postEncerrarMdfe } from '../../../../../services/api/https/deliveries/mdfe.http'
import { postEncerrarCiot } from '../../../../../services/api/https/deliveries/ciot.http'
import { errorHandler } from '../../../../../services/util/error.service'
import SelectMunicipioCiot from '../../Ciot/SelectMunicipioCiot'
import {
  CIOT_LOTACAO,
  NOMES_TIPO_EMITENTE_MDFE,
  estiloDicaCiot,
  rotuloCiot,
  ufIdDoMunicipio,
} from '../../Ciot/ciot.util'

const rotuloMdfe = mdfe => (mdfe.numero ? `MDFe nº ${mdfe.numero}` : `MDFe ref. ${mdfe.ref}`)

export default function ModalEncerrarViagem({
  visible,
  mdfes,
  ciots,
  onConcluido,
  onClose,
  onError,
}) {
  const [ufs, setUfs] = useState([])
  const [selecaoMdfes, setSelecaoMdfes] = useState({})
  const [selecaoCiots, setSelecaoCiots] = useState({})
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    getUfs().then(setUfs).catch(onError)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!visible) return
    setSelecaoMdfes(
      (mdfes || []).reduce((selecao, mdfe) => {
        const sugestao = mdfe.encerramento_sugerido || {}
        selecao[mdfe.id] = {
          marcado: true,
          codigo: sugestao.city_id || null,
          nome: sugestao.nome || null,
          uf: sugestao.uf || mdfe.uf_fim || null,
        }
        return selecao
      }, {}),
    )
    setSelecaoCiots(
      (ciots || []).reduce((selecao, ciot) => {
        selecao[ciot.id] = { marcado: true, peso: ciot.peso_carga }
        return selecao
      }, {}),
    )
  }, [visible, mdfes, ciots])

  const alterarMdfe = (id, campos) =>
    setSelecaoMdfes(prev => ({ ...prev, [id]: { ...prev[id], ...campos } }))

  const alterarCiot = (id, campos) =>
    setSelecaoCiots(prev => ({ ...prev, [id]: { ...prev[id], ...campos } }))

  const ufDaCidade = codigo => {
    const uf = ufs.find(item => item.id === ufIdDoMunicipio(codigo))
    return uf ? uf.uf : null
  }

  const onBtnEncerrarClicked = async () => {
    const mdfesMarcados = (mdfes || []).filter(mdfe => (selecaoMdfes[mdfe.id] || {}).marcado)
    const ciotsMarcados = (ciots || []).filter(ciot => (selecaoCiots[ciot.id] || {}).marcado)
    if (!mdfesMarcados.length && !ciotsMarcados.length) {
      onError('Marque ao menos um documento para encerrar, ou clique em "Agora não"')
      return
    }
    const semCidade = mdfesMarcados.find(mdfe => !selecaoMdfes[mdfe.id].codigo)
    if (semCidade) {
      onError(`${rotuloMdfe(semCidade)}: informe o Estado e a Cidade de encerramento`)
      return
    }
    const semPeso = ciotsMarcados.find(
      ciot =>
        Number(ciot.tipo_operacao) === CIOT_LOTACAO && !(Number(selecaoCiots[ciot.id].peso) > 0),
    )
    if (semPeso) {
      onError(`CIOT ${rotuloCiot(semPeso)}: informe o peso da carga para encerrar`)
      return
    }

    setEnviando(true)
    const resultados = []
    for (const mdfe of mdfesMarcados) {
      const { codigo, nome } = selecaoMdfes[mdfe.id]
      try {
        await postEncerrarMdfe({
          ref: mdfe.ref,
          sigla_uf: ufDaCidade(codigo),
          nome_municipio: nome,
        })
        resultados.push({ sucesso: true, texto: `${rotuloMdfe(mdfe)}: encerramento enviado` })
      } catch (error) {
        resultados.push({ sucesso: false, texto: `${rotuloMdfe(mdfe)}: ${errorHandler(error)}` })
      }
    }
    for (const ciot of ciotsMarcados) {
      try {
        await postEncerrarCiot(ciot.id, selecaoCiots[ciot.id].peso)
        resultados.push({ sucesso: true, texto: `CIOT ${rotuloCiot(ciot)}: encerrado na ANTT` })
      } catch (error) {
        resultados.push({
          sucesso: false,
          texto: `CIOT ${rotuloCiot(ciot)}: ${errorHandler(error)}`,
        })
      }
    }
    setEnviando(false)
    onConcluido(resultados)
  }

  return (
    <CustomModal
      isVisible={visible}
      header="Encerrar documentos da viagem"
      style={{ width: '90vw', maxWidth: 800 }}
      contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
      onClose={onClose}
    >
      <div className="p-grid">
        <div className="p-col-12" style={estiloDicaCiot}>
          O veículo retornou. Deseja encerrar o MDFe e o CIOT desta viagem? Enquanto o MDFe estiver
          aberto, a SEFAZ recusa outro MDFe do mesmo veículo para a mesma UF de descarga.
        </div>

        {(mdfes || []).length > 0 && (
          <div className="p-col-12">
            <SectionTitle title="MDFe" />
          </div>
        )}
        {(mdfes || []).map(mdfe => {
          const selecao = selecaoMdfes[mdfe.id] || {}
          return (
            <React.Fragment key={mdfe.id}>
              <div className="p-md-5 p-col-12" style={{ paddingTop: 30 }}>
                <CustomCheckbox
                  checked={Boolean(selecao.marcado)}
                  onChange={value => alterarMdfe(mdfe.id, { marcado: value })}
                >
                  {rotuloMdfe(mdfe)} ({mdfe.uf_inicio} → {mdfe.uf_fim})
                </CustomCheckbox>
                <div style={{ ...estiloDicaCiot, marginLeft: 25 }}>
                  {NOMES_TIPO_EMITENTE_MDFE[mdfe.tipo_emitente] || ''}
                </div>
              </div>
              <div className="p-md-7 p-col-12">
                <SelectMunicipioCiot
                  labelUf="Estado:*"
                  labelCidade="Cidade de encerramento:*"
                  codigo={selecao.codigo}
                  ufSigla={selecao.uf}
                  ufs={ufs}
                  onError={onError}
                  onChange={cidade =>
                    alterarMdfe(mdfe.id, {
                      codigo: cidade ? cidade.codigo : null,
                      nome: cidade ? cidade.nome : null,
                    })
                  }
                />
              </div>
            </React.Fragment>
          )
        })}

        {(ciots || []).length > 0 && (
          <div className="p-col-12" style={{ marginTop: 10 }}>
            <SectionTitle title="CIOT" />
          </div>
        )}
        {(ciots || []).map(ciot => {
          const selecao = selecaoCiots[ciot.id] || {}
          const lotacao = Number(ciot.tipo_operacao) === CIOT_LOTACAO
          return (
            <React.Fragment key={ciot.id}>
              <div className="p-md-5 p-col-12" style={{ paddingTop: 30 }}>
                <CustomCheckbox
                  checked={Boolean(selecao.marcado)}
                  onChange={value => alterarCiot(ciot.id, { marcado: value })}
                >
                  CIOT {rotuloCiot(ciot)} ({lotacao ? 'lotação' : 'fracionada'})
                </CustomCheckbox>
              </div>
              <div className="p-md-3 p-col-12">
                {lotacao && (
                  <InputNumeroDecimal
                    label="Peso da carga (kg):*"
                    marginTop={10}
                    value={selecao.peso}
                    onChange={value => alterarCiot(ciot.id, { peso: value })}
                  />
                )}
              </div>
              <div className="p-md-4 p-col-12" />
            </React.Fragment>
          )
        })}

        <div
          className="p-col-12"
          style={{ display: 'flex', justifyContent: 'space-between', marginTop: 15 }}
        >
          <Btn backgroundColor="#999999" onClick={onClose}>
            Agora não
          </Btn>
          <Btn isLoading={enviando} onClick={onBtnEncerrarClicked}>
            Encerrar selecionados
          </Btn>
        </div>
      </div>
    </CustomModal>
  )
}
